// Users list — Session C migration to the FwTable pattern.
// Same request shape as the AgTable page it replaces; columns from
// agModule.json; permission gates unchanged.
//
// Adds the super-only power tools: a PII CSV export of whatever the current
// filter shows, and bulk state changes. Both are gated here for the UI's sake
// and again on the server, because the portal is not the only thing that can
// call those endpoints.
import { Fragment, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import ModuleListPage from "elements/Table/ModuleListPage";
import { api, common, config } from "includes";

type Pending = { action: string; role?: string; ids: string[]; done: () => void };

const ACTIONS = [
  { key: "activate", label: "Activate", variant: "light" },
  { key: "deactivate", label: "Deactivate", variant: "light" },
  { key: "flag", label: "Flag", variant: "light" },
  { key: "unflag", label: "Unflag", variant: "light" },
];

// Super is deliberately absent. Promoting someone to super is a decision made
// one record at a time on the user form, never by rubber-banding a table.
const BULK_ROLES = [
  { key: "basic_user", label: "Basic User" },
  { key: "premium_free", label: "Premium Free" },
];

export default function List() {
  const [auth] = useState<any>(common.getAuth());
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const isSuper = auth?.role === "super";

  const run = () => {
    if (!pending) return;
    setBusy(true);
    api.call(
      {
        url: "admin/user/bulk-update",
        method: "POST",
        auth: "token",
        body: { ids: pending.ids, action: pending.action, role: pending.role },
      },
      (res: any) => {
        setBusy(false);
        if (res.status === 200) {
          const d = res.data || {};
          common.notify(
            "S",
            d.skipped
              ? `${d.updated} updated · ${d.skipped} skipped (super admins and your own account are never changed in bulk)`
              : `${d.updated} user${d.updated === 1 ? "" : "s"} updated`
          );
          pending.done();
        } else {
          common.notify("E", res?.data?.error || "Bulk update failed");
        }
        setPending(null);
      }
    );
  };

  // The CSV comes back as a file, not JSON, so it goes through fetch directly
  // rather than api.call — and the token is sent as a header rather than in the
  // URL, so a PII export never lands in browser history or a proxy log.
  const exportCsv = async (filter: Record<string, any>) => {
    try {
      const query = `?query=${encodeURIComponent(JSON.stringify(filter || {}))}`;
      const res = await fetch(`${config.api.url}admin/user/export${query}`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
      });
      if (!res.ok) throw new Error(String(res.status));

      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `firewire-users-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(href);
      common.notify("S", "Export downloaded");
    } catch {
      common.notify("E", "Could not export users");
    }
  };

  const label = () => {
    if (!pending) return "";
    if (pending.action === "role") {
      return `change the role of ${pending.ids.length} user${pending.ids.length === 1 ? "" : "s"} to ${
        BULK_ROLES.find((r) => r.key === pending.role)?.label
      }`;
    }
    const verb = ACTIONS.find((a) => a.key === pending.action)?.label.toLowerCase();
    return `${verb} ${pending.ids.length} user${pending.ids.length === 1 ? "" : "s"}`;
  };

  return (
    <Fragment>
      <ModuleListPage
        module="users"
        title="Users"
        api="admin/user"
        url="user"
        columnsKey="users"
        quickKey="firstName"
        quickPlaceholder="Filter by first name…"
        perms={["usr", "usc", "usu", "usd"]}
        bulkActions={
          isSuper
            ? (ids, done, filter) => (
                <Fragment>
                  <button
                    type="button"
                    className="btn btn-light btn-sm"
                    onClick={() => exportCsv(filter)}
                    title="Download the users this filter shows, as CSV"
                  >
                    <i className="ri-download-2-line" /> Export CSV
                  </button>
                  {ACTIONS.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      className="btn btn-light btn-sm"
                      disabled={!ids.length}
                      onClick={() => setPending({ action: a.key, ids, done })}
                    >
                      {a.label}
                      {ids.length ? ` (${ids.length})` : ""}
                    </button>
                  ))}
                  {BULK_ROLES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      className="btn btn-light btn-sm"
                      disabled={!ids.length}
                      onClick={() =>
                        setPending({ action: "role", role: r.key, ids, done })
                      }
                    >
                      → {r.label}
                    </button>
                  ))}
                </Fragment>
              )
            : undefined
        }
      />

      <Modal show={Boolean(pending)} onHide={() => setPending(null)}>
        <Modal.Header closeButton>
          <h4 className="modal-title">Confirm bulk change</h4>
        </Modal.Header>
        <Modal.Body>
          <p className="fs-16 mb-1">You are about to {label()}.</p>
          <p className="text-muted mb-0">
            Super admin accounts and your own account are never changed in bulk —
            they are skipped and reported back to you.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" disabled={busy} onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={run}>
            {busy ? "Working…" : "Apply"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
